import useSWR from "swr";

import { HttpHeaderNames } from "@touchtech/web-api-client";

import { api } from "@is/services";
import { createConfigurationUrl } from "@is/services/api";
import { SettingsConfiguration } from "@is/types";
import { getEnvValue } from "@is/utils/env";

type SWRConfiguration = {
  [key: string]: boolean;
};

// Shared fallbacks, not fresh `[]` literals: consumers memoize on these arrays
// and re-run effects when the reference changes, so a per-render empty array
// keeps derived work (and its requests) firing for as long as the
// configuration is missing those fields.
const EMPTY_CUSTOM_ATTRIBUTES: SettingsConfiguration["customAttributes"] = [];
const EMPTY_ATTRIBUTES: SettingsConfiguration["attributes"] = [];
const EMPTY_CHECKOUT_AGREEMENTS: SettingsConfiguration["checkoutAgreements"] =
  [];
const EMPTY_LOCALIZED_MEDIA: SettingsConfiguration["localizedMedia"] = [];

export const useConfiguration = (
  swrConfig: SWRConfiguration = {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
  }
) => {
  const url = createConfigurationUrl();

  const headers = new Headers({
    [HttpHeaderNames.APPLICATION_ID]: getEnvValue(
      "VITE_APPLICATION_ID"
    ) as string,
  });

  const { data, error, isLoading } = useSWR(
    url,
    () => api.get(url, headers, true),
    swrConfig
  );

  const configuration = data as SettingsConfiguration;

  return {
    data: configuration,
    appearance: configuration?.appearance,
    customAttributes:
      configuration?.customAttributes ?? EMPTY_CUSTOM_ATTRIBUTES,
    attributes: configuration?.attributes ?? EMPTY_ATTRIBUTES,
    backgroundMedia: configuration?.backgroundMedia,
    checkoutAgreements:
      configuration?.checkoutAgreements ?? EMPTY_CHECKOUT_AGREEMENTS,
    checkoutGeneral: configuration?.checkoutGeneral,
    basketAiSuggestions: configuration?.basketAiSuggestions,
    localizedMedia: configuration?.localizedMedia ?? EMPTY_LOCALIZED_MEDIA,
    loyaltySignupForm: configuration?.loyaltySignupForm,
    productDetailsView: configuration?.productDetailsView,
    staffDashboard: configuration?.staffDashboard,
    timeouts: configuration?.timeouts,
    isLoading,
    error,
  };
};
